import type { CSSProperties, JSX, ReactNode } from 'react'
import { Suspense, useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'

import type { FileRouteViewMode } from '@jotta/types/schemas/FileRouteSchema'
import { LoadingOverlay } from '@jotta/ui/LoadingOverlay'
import { useBrandStore } from '@jotta/ui/useBrandTheme'
import { cn } from '@jotta/utils/css'

import { FileListRow } from './FileListRow'
import { FileListStore } from './FileListStore'

import styles from './FileList.module.scss'

export type FileListRenderItem = (
  index: number,
  key: string,
  viewMode: FileRouteViewMode,
) => JSX.Element

export interface FileListProps {
  itemCount: number
  renderItem: FileListRenderItem
  emptyContent?: ReactNode
  showEmptyContentWhenEmpty?: boolean
  viewMode: FileRouteViewMode
  className?: string
  disabled?: boolean
  loading?: boolean
  onRenderItemCount?: (count: number) => void
}

export const FileListRows = observer(function FileList({
  store,
}: {
  store: FileListStore
}) {
  return (
    <>
      {store.visibleRows.map(row => (
        <FileListRow key={row.key} row={row} />
      ))}
    </>
  )
})
export const FileList = observer(function FileList({
  className,
  itemCount,
  renderItem,
  emptyContent = null,
  showEmptyContentWhenEmpty = false,
  viewMode,
  disabled = false,
  onRenderItemCount,
  loading = false,
}: FileListProps) {
  const branding = useBrandStore()
  const [store] = useState(
    () => new FileListStore(renderItem, viewMode, itemCount, branding),
  )
  const isEmpty = !itemCount && showEmptyContentWhenEmpty
  if (
    itemCount !== store.itemCount ||
    viewMode !== store.viewMode ||
    renderItem !== store.renderItem
  ) {
    store.sync({ itemCount, viewMode, renderItem })
  }

  useEffect(() => {
    if (!itemCount && !isEmpty) {
      return
    }
    if (onRenderItemCount) {
      onRenderItemCount(itemCount)
    }
  }, [itemCount, onRenderItemCount, isEmpty])

  return (
    <Suspense fallback={<LoadingOverlay id="FileList" />}>
      <div
        data-testid={
          viewMode === 'list' ? 'FolderViewList' : 'FolderViewThumbs'
        }
        className={cn(styles.container, className, {
          viewmodelist: viewMode === 'list',
          viewmodethumbs: viewMode === 'thumbs',
          [styles.empty]: isEmpty,
          [styles.thumbs]: viewMode === 'thumbs' && !isEmpty,
          [styles.disabled]: disabled,
        })}
        style={
          {
            '--virtual-columns': `${store.columns}`,
            '--virtual-row-height': `${store.rowHeight}px`,
            height: store.listHeight || 'auto',
          } as CSSProperties
        }
      >
        {isEmpty ? emptyContent : <FileListRows store={store} />}
      </div>
    </Suspense>
  )
})
